import type { RequestFieldDefinition } from '../types';

export const OPPORTUNITY_SUBSECTION_ORDER = [
  'requestor',
  'projectSummary',
  'projectDetails',
  'scaleTimelineBudget',
  'team',
  'additionalInformation',
] as const;

export type OpportunitySubsectionId =
  (typeof OPPORTUNITY_SUBSECTION_ORDER)[number];

/** User-visible subsection headings on the Opportunity step and Summary card. */
export const OPPORTUNITY_SUBSECTION_DISPLAY_TITLES: Readonly<
  Record<OpportunitySubsectionId, string>
> = {
  requestor: 'Requestor',
  projectSummary: 'Project Summary',
  projectDetails: 'Project Details',
  scaleTimelineBudget: 'Feasibility',
  team: 'Team Information',
  additionalInformation: 'Additional Information',
} as const;

const step = 'opportunity' as const;

/**
 * Opportunity step + Summary — single ordered field list.
 * Edit only here when adding, removing, or reordering Opportunity fields.
 */
export const OPPORTUNITY_FIELDS: readonly RequestFieldDefinition[] = [
  {
    dataField: 'requestorName',
    stepId: step,
    subsectionId: 'requestor',
    order: 0,
    label: 'Requestor Name',
    shortLabel: 'Name',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'requestorRole',
    stepId: step,
    subsectionId: 'requestor',
    order: 1,
    label: 'Requestor Role',
    shortLabel: 'Role',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'requestorEmail',
    stepId: step,
    subsectionId: 'requestor',
    order: 2,
    label: 'Requestor Email',
    shortLabel: 'Email',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'requestorPhone',
    stepId: step,
    subsectionId: 'requestor',
    order: 3,
    label: 'Requestor Phone',
    shortLabel: 'Phone',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'region',
    stepId: step,
    subsectionId: 'projectSummary',
    order: 0,
    label: 'Region',
    shortLabel: 'Region',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'customerName',
    stepId: step,
    subsectionId: 'projectSummary',
    order: 1,
    label: 'Customer name',
    shortLabel: 'Customer',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'partnerName',
    stepId: step,
    subsectionId: 'projectSummary',
    order: 2,
    label: 'Channel Partner',
    shortLabel: 'Channel Partner',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'oppProjectName',
    stepId: step,
    subsectionId: 'projectSummary',
    order: 3,
    label: 'Project name',
    shortLabel: 'Project',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'oppUsecase',
    stepId: step,
    subsectionId: 'projectSummary',
    order: 4,
    label: 'Use case',
    shortLabel: 'Use case',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'oppWorkloadType',
    stepId: step,
    subsectionId: 'projectSummary',
    order: 5,
    label: 'Is this an AI project or traditional compute?',
    shortLabel: 'Workload',
    surfaces: { step: true, summary: true },
  },
  // Smart IT NVL configs (migration 092) sit directly under the workload pick that
  // gates them. Each Summary row renders ONLY while its gate is selected — then
  // "Not specified" is valid if the config itself is unanswered. Gate values must
  // match OPP_WORKLOAD_TYPE_OPTIONS (server/constants/opportunity.ts is the SoT).
  {
    dataField: 'smartItNvl72Config',
    stepId: step,
    subsectionId: 'projectSummary',
    order: 6,
    label: 'Vertiv™ SmartIT - Enterprise AI for Blackwell NVL72 GB300',
    shortLabel: 'NVL72 config',
    surfaces: { step: true, summary: true },
    summaryVisibleWhenFieldContains: {
      dataField: 'oppWorkloadType',
      value: 'Vertiv™ SmartIT - Enterprise AI for Blackwell NVL72 GB300',
    },
  },
  {
    dataField: 'smartItNvl8Config',
    stepId: step,
    subsectionId: 'projectSummary',
    order: 7,
    label: 'Vertiv™ SmartIT - Enterprise AI for Rubin NVL8',
    shortLabel: 'NVL8 config',
    surfaces: { step: true, summary: true },
    summaryVisibleWhenFieldContains: {
      dataField: 'oppWorkloadType',
      value: 'Vertiv™ SmartIT - Enterprise AI for Rubin NVL8',
    },
  },
  {
    dataField: 'oppProjectDescription',
    stepId: step,
    subsectionId: 'projectSummary',
    order: 8,
    label: 'Project description',
    shortLabel: 'Description',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'dealRegistrationNumber',
    stepId: step,
    subsectionId: 'projectDetails',
    order: 0,
    label: 'Deal registration number',
    shortLabel: 'Deal',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'oppOpportunityId',
    stepId: step,
    subsectionId: 'projectDetails',
    order: 1,
    label: 'Opportunity number',
    shortLabel: 'Opportunity',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'oppQuoteNumber',
    stepId: step,
    subsectionId: 'projectDetails',
    order: 2,
    label: 'Quote Number',
    shortLabel: 'Quote',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'oppProjectType',
    stepId: step,
    subsectionId: 'projectDetails',
    order: 3,
    label: 'Project type',
    shortLabel: 'Type',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'oppProjectScope',
    stepId: step,
    subsectionId: 'projectDetails',
    order: 4,
    label: 'Project scope',
    shortLabel: 'Scope',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'oppVertical',
    stepId: step,
    subsectionId: 'projectDetails',
    order: 5,
    label: 'Vertical',
    shortLabel: 'Vertical',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'oppSiteAssessmentRequired',
    stepId: step,
    subsectionId: 'projectDetails',
    order: 6,
    label: 'Is Site Assessment Required?',
    shortLabel: 'Assessment',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'oppPreferredAssessmentDate',
    stepId: step,
    subsectionId: 'projectDetails',
    order: 7,
    label: 'Preferred assessment date',
    shortLabel: 'Date',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'oppSiteAddress',
    stepId: step,
    subsectionId: 'projectDetails',
    order: 8,
    label: 'Site address',
    shortLabel: 'Address',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'oppDeploymentTimeline',
    stepId: step,
    subsectionId: 'scaleTimelineBudget',
    order: 0,
    label: 'Project deployment timeline',
    shortLabel: 'Timeline',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'oppApproximateKw',
    stepId: step,
    subsectionId: 'scaleTimelineBudget',
    order: 1,
    label: 'Approximate kW for project (if known)',
    shortLabel: 'kW',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'oppBudget',
    stepId: step,
    subsectionId: 'scaleTimelineBudget',
    order: 2,
    label: 'Budget (if known)',
    shortLabel: 'Budget',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'vertivSponsors',
    stepId: step,
    subsectionId: 'team',
    order: 0,
    label: 'Vertiv sponsors',
    shortLabel: 'Sponsors',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'vertivContributors',
    stepId: step,
    subsectionId: 'team',
    order: 1,
    label: 'Vertiv contributors',
    shortLabel: 'Contributors',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'partnerContributors',
    stepId: step,
    subsectionId: 'team',
    order: 2,
    label: 'Partner contributors',
    shortLabel: 'Partners',
    surfaces: { step: true, summary: true },
  },
  {
    dataField: 'siteAdditionalSiteInformation',
    stepId: step,
    subsectionId: 'additionalInformation',
    order: 0,
    label: 'Additional Opportunity Information',
    shortLabel: 'Notes',
    surfaces: { step: true, summary: true },
  },
];
